/**
 * Authentication context provider — GitHub OAuth flow.
 *
 * Centralizes all auth state so login/logout cleanly sets up and tears
 * down all dependent state. Route guards consume this context.
 *
 * CRITICAL: The provider initializes with isLoading: true. Route guards MUST
 * show a skeleton/spinner until isLoading resolves to false. This prevents
 * flashing the login page during token refresh (terrible UX).
 *
 * Auth hooks expose:
 *   user            — current user or null
 *   isAuthenticated — boolean (derived from user !== null)
 *   isLoading       — true while auth state is initializing or refreshing
 *   permissions     — set of permission slugs for the current user
 *   login()         — redirect to GitHub OAuth
 *   logout()        — clear state, cache, and redirect
 *
 * @spec SPEC:ENGINEERING:FRONTEND:AUTH
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { getGitHubLoginUrl, clearAuth } from '@/lib/auth'
import { events } from '@/services/events'
import { logger } from '@/services/logger'

// ── Types ─────────────────────────────────────────────────────────────────────

export interface AuthUser {
  id: string
  email: string
  name: string
  role: string
  avatarUrl?: string
  /** Flat set of permission slugs — checked by RequirePermission wrapper */
  permissions: Set<string>
}

interface AuthContextValue {
  user: AuthUser | null
  isAuthenticated: boolean
  /** True while auth is initializing or refreshing — guards show skeleton */
  isLoading: boolean
  permissions: Set<string>
  login: () => void
  logout: () => Promise<void>
  /**
   * Re-fetches the current user from /auth/me using the stored token.
   * Call this after a successful token exchange to sync React state
   * without a full page reload (e.g. after OAuth callback).
   */
  refreshSession: () => Promise<void>
}

// ── Context ────────────────────────────────────────────────────────────────────

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()
  // Initialize with isLoading: true — guards show skeleton until resolved
  const [user, setUser] = useState<AuthUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // On mount: restore session from stored token
  useEffect(() => {
    let cancelled = false
    async function restoreSession() {
      try {
        const token = api.getToken()
        if (!token) {
          if (!cancelled) setUser(null)
          return
        }

        const me = await api.get<{
          id: string
          email: string
          name: string
          role: string
          avatarUrl?: string
          orgId?: string
        }>('/auth/me')

        if (!cancelled) {
          const currentUser: AuthUser = {
            ...me,
            permissions: new Set(['projects.read', 'projects.create']),
          }
          setUser(currentUser)
          events.identify(currentUser.id, { email: currentUser.email, name: currentUser.name, role: currentUser.role })
          if (me.orgId) {
            events.register({ orgId: me.orgId })
          }
        }
      } catch (err) {
        logger.error('auth.restore failed', err)
        if (!cancelled) {
          clearAuth()
          setUser(null)
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    void restoreSession()
    return () => { cancelled = true }
  }, [])

  const refreshSession = useCallback(async () => {
    try {
      const token = api.getToken()
      if (!token) return
      const me = await api.get<{
        id: string
        email: string
        name: string
        role: string
        avatarUrl?: string
        orgId?: string
      }>('/auth/me')
      const currentUser: AuthUser = {
        ...me,
        permissions: new Set(['projects.read', 'projects.create']),
      }
      setUser(currentUser)
      events.identify(currentUser.id, { email: currentUser.email, name: currentUser.name, role: currentUser.role })
      if (me.orgId) {
        events.register({ orgId: me.orgId })
      }
    } catch (err) {
      logger.error('auth.refreshSession failed', err)
    }
  }, [])

  const login = useCallback(() => {
    window.location.href = getGitHubLoginUrl()
  }, [])

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout')
      events.track('user.logout')
      events.reset()
      // Clear ALL cached query data — prevents data leakage between users
      await queryClient.resetQueries()
      queryClient.clear()
    } catch (err) {
      logger.error('auth.logout failed', err)
    } finally {
      clearAuth()
      setUser(null)
      logger.info('auth.logout completed')
    }
  }, [queryClient])

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated: user !== null,
      isLoading,
      permissions: user?.permissions ?? new Set<string>(),
      login,
      logout,
      refreshSession,
    }),
    [user, isLoading, login, logout, refreshSession]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// ── Hooks ──────────────────────────────────────────────────────────────────────

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}

export function usePermission(slug: string): boolean {
  const { permissions } = useAuth()
  return permissions.has(slug)
}
